import { AlertCircle, Loader2 } from 'lucide-preact'
import { HtmlPreview } from '@/components/webs/landingPage/HtmlPreview'
import { useLandingPageTemplatePreview } from '@/hooks/useLandingPageTemplatePreview'
import type { LandingPageTemplateListItemDto } from '@/types/landingPage'
import { templateHasEventStates } from './templateText'

interface TemplatePreviewPaneProps {
    workspaceId: string
    template: LandingPageTemplateListItemDto
}

/**
 * Both pages of the version a start from this template would copy now, with the builder's own Desktop / Tablet /
 * Mobile and Landing Page / Thank You controls. A live event's template opens on its pre-event state, since the event
 * it was saved from has usually happened.
 */
export function TemplatePreviewPane({ workspaceId, template }: TemplatePreviewPaneProps) {
    const { preview, loading, error } = useLandingPageTemplatePreview(workspaceId, template.WebId)

    if (error) {
        return (
            <div role="alert" className="flex-1 flex flex-col items-center justify-center gap-2 px-6 bg-slate-100 dark:bg-slate-950 text-center">
                <p className="flex items-center gap-2 text-sm font-medium text-slate-900 dark:text-white">
                    <AlertCircle className="w-4 h-4 text-red-600 dark:text-red-400" aria-hidden="true" />
                    The preview could not load.
                </p>
                <p className="max-w-md text-sm text-slate-600 dark:text-slate-300">{error}</p>
            </div>
        )
    }

    if (loading || !preview) {
        return (
            <div className="flex-1 flex items-center justify-center bg-slate-100 dark:bg-slate-950">
                <Loader2 className="w-5 h-5 animate-spin text-slate-400" aria-hidden="true" />
                <span className="sr-only" role="status">Loading the preview</span>
            </div>
        )
    }

    return (
        <div className="flex-1 min-h-0">
            <HtmlPreview
                key={template.WebId}
                html={preview.Html ?? null}
                thankYouHtml={preview.ThankYouHtml ?? null}
                hasEventStates={templateHasEventStates(template)}
            />
        </div>
    )
}
